// API calls for the new admin area (src/pages/Admin).
// Uses its own axios instance so admin errors never trigger the customer-side
// "page not found" screen, uploads report failures, and an ended session can
// send the admin back to the sign-in page.
import axios from 'axios'

import { API_ROOT, AUTH_TOKEN } from '../utils/constants'
import localStorage from '../utils/localStorage'

const client = axios.create({ baseURL: API_ROOT })

let sessionEndedHandler = null

// The admin layout registers what to do when the server says the session is over
export const onSessionEnded = (handler) => {
  sessionEndedHandler = handler
  return () => {
    if (sessionEndedHandler === handler) sessionEndedHandler = null
  }
}

client.interceptors.request.use((config) => {
  // Sign-in must go out without a token: the server only accepts it from someone
  // who is not already signed in, and a customer sign-in may be in localStorage.
  const token = config.skipAuth ? null : localStorage.getItem(AUTH_TOKEN)
  config.headers = {
    ...config.headers,
    'X-Referer': global.window?.location?.href || '/',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
  return config
})

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    if (status === 401 && !error?.config?.skipSessionCheck && sessionEndedHandler) {
      sessionEndedHandler()
    }
    return Promise.reject(error)
  }
)

// Turns any failed request into one sentence the admin can act on
export const errorText = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback
  if (!error.response) {
    return error.message === 'Network Error'
      ? 'Could not reach the Morsel server. Check your connection and try again.'
      : error.message || fallback
  }
  const { status, data } = error.response
  if (status === 401) return 'Your admin session ended, please sign in again.'
  let body = data
  if (body instanceof ArrayBuffer) {
    try {
      body = JSON.parse(new TextDecoder().decode(body))
    } catch (e) {
      body = null
    }
  }
  // Family Pro: the email already gets Family Pro from someone else
  if (Number(body?.code) === 10018) {
    return 'That person already has Family Pro through someone else, so they cannot be added.'
  }
  if (Number(body?.code) === 10017) {
    return 'There is no active subscription for this account in Chargebee, so this cannot be changed here.'
  }
  const message = body?.data ?? body?.message ?? body
  if (typeof message === 'string' && message.trim() && !message.trim().startsWith('<')) {
    // Server codes look like "E_SOMETHING: text" or "10017: text"; keep only the text
    return message.replace(/^(E_[A-Z_]+|\d+):\s*/, '')
  }
  if (Array.isArray(message) && message.length) {
    const first = message[0]
    return (first?.message || first?.field || String(first)) + ''
  }
  // Validation errors come back as { field: 'field must ...' }
  if (message && typeof message === 'object') {
    const [field, text] = Object.entries(message)[0] || []
    if (typeof text === 'string') {
      const name = String(field).replace(/_/g, ' ')
      return /must match/.test(text) ? `That ${name} has characters that are not allowed.` : text
    }
  }
  if (status === 403) return 'This admin account is not allowed to do that.'
  if (status === 404) return 'That could not be found. It may have been deleted.'
  if (status === 413) return 'That file is too big to upload.'
  if (status >= 500) return 'The server had a problem. Please try again in a minute.'
  return fallback
}

const unwrap = (response) => response?.data?.data

const get = (url, params) => client.get(url, { params }).then(unwrap)
const put = (url, data) => client.put(url, data).then(unwrap)
const post = (url, data) => client.post(url, data).then(unwrap)
const del = (url, data) => client.delete(url, { data }).then(unwrap)

const form = (data) => {
  const formData = new FormData()
  Object.entries(data).forEach(([key, value]) => {
    // Same encoding as the old admin (null becomes "null"), which the server expects
    if (value !== undefined) formData.append(key, value)
  })
  return formData
}
const upload = (url, data, method = 'post') =>
  client({ url, method, data: form(data) }).then(unwrap)

const getFile = (url, params) =>
  client.get(url, { params, responseType: 'arraybuffer' }).then((response) => response.data)

// Sign in (sent without any existing token; never triggers the "session ended" handler)
export const login = (params) =>
  client.post('/admin/login', params, { skipAuth: true, skipSessionCheck: true }).then(unwrap)

// Today
// period: 'today' | 'week' | '30d'
export const getOverview = (period) => get('/admin/overview', period ? { period } : undefined)
export const search = (q) => get('/admin/search', { q })

// People
export const getPeople = (params) => get('/admin/people', params)
export const getPerson = (id) => get(`/admin/people/${id}`)
export const setPersonAccess = (id, paused) => put(`/admin/people/${id}/access`, { paused })
export const getPersonBilling = (id) => get(`/admin/people/${id}/billing`)
export const getPersonLookalikes = (id) => get(`/admin/people/${id}/lookalikes`)

// Second accounts: look-alike pairs. decision 'same' | 'different', or null to undo
export const getSecondAccounts = () => get('/admin/second-accounts')
export const reviewPair = (userA, userB, decision, note) =>
  put('/admin/second-accounts', { user_a: userA, user_b: userB, decision, note })
export const giveFreePro = (id, until, note) => put(`/admin/people/${id}/free-pro`, { until, note })
export const endFreePro = (id) => del(`/admin/people/${id}/free-pro`)
export const closePerson = (id) => post(`/admin/people/${id}/close`)
export const updatePerson = (id, params) => put(`/admin/users/${id}`, params)
export const setCookbookBeta = (id, enabled) => put(`/admin/users/${id}/cookbook-beta`, { enabled })
// New features (gifts, ...) switched on or off for one person
export const setFeature = (id, name, enabled) =>
  put(`/admin/users/${id}/features`, { name, enabled })
// Always send the full list of email strings that should remain shared
export const setSharedEmails = (id, emails) => put(`/admin/users/${id}/shared-emails`, { emails })
export const removeFromGroup = (userId, groupId) => del(`/admin/users/${userId}/groups/${groupId}`)
export const sendPasswordReset = (email) => put('/users/password/reset', { email })

// Family groups
export const getGroups = (params) => get('/admin/groups-list', params)
export const getGroup = (id) => get(`/admin/groups/${id}`)
export const renameGroup = (id, name) => put(`/admin/groups/${id}`, { name })
export const deleteGroup = (id) => del(`/admin/groups/${id}`)
export const makeGroupOwner = (groupId, userId) =>
  put(`/admin/groups/${groupId}/owner`, { user_id: userId })
export const removeRecipeFromGroup = (groupId, recipeId) =>
  del(`/admin/groups/${groupId}/recipes/${recipeId}`)

// Recipes
export const getRecipes = (params) => get('/admin/recipes-list', params)
export const getRecipe = (id) => get(`/admin/recipes/${id}`)
export const getRecipeReports = (id) => get(`/admin/recipes/${id}/reports`)
export const keepRecipe = (id) => del(`/admin/reports/recipes/${id}`)
export const deleteRecipe = (id) => del(`/admin/recipes/${id}`)
export const updateRecipe = (params) => put(`/admin/recipes/${params?.id}`, params)
export const updateRecipePhoto = (params) => upload(`/admin/recipes/${params?.id}`, params, 'put')
// filters: { ids: [..] } or { group_id } or { user_id }
export const exportRecipes = (filters) => getFile('/admin/recipes/export', filters)

// Cookbook orders
export const getCookbookOrders = () => get('/admin/cookbook-orders')
// Cookbook journeys: who started a cookbook, how far they got, one draft's timeline
export const getCookbookJourneys = (params) => get('/admin/cookbook-journeys', params)
export const getCookbookJourney = (id) => get(`/admin/cookbook-journeys/${id}`)
// "Request my book": people who asked for a cookbook without paying online
export const getCookbookRequests = () => get('/admin/cookbook-orders/requests')
export const updateCookbookRequest = (id, data) =>
  put(`/admin/cookbook-orders/requests/${id}`, data)
export const retryCookbookOrder = (id) => post(`/admin/cookbook-orders/${id}/retry`)
export const refundCookbookOrder = (id) => post(`/admin/cookbook-orders/${id}/refund`)

// Site content
export const getAd = (isDesktop) => get('/ads', { is_desktop: isDesktop })
export const saveAd = (params) => upload('/admin/ads', params, 'post')
export const deleteAd = (isDesktop) => del('/admin/ads', { is_desktop: isDesktop })
export const getVideo = () => get('/recipes/video')
export const saveVideo = (params) => upload('/admin/recipes/video', params, 'post')
